import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { addConfig, addContext, configConsumer } from '@estee/elc-service';
import type { ISliderSettings } from '@estee/elc-slider';
import { Column } from '@estee/elc-grid';
import type { IconTypes } from '@estee/elc-accordion';
import { contentSize } from '../../internal/constants/ProductGrid';
import type { ProductGridViewController } from '../../internal/controllers/ProductGridViewController';
import { CartLimitReachedModal } from '../../internal/views/cart-limit-modal/CartLimitReachedModal';
import { ProductAccordion } from '../../internal/views/product-accordion/ProductAccordion';
import { mergeConfigsFor } from '../../internal/utils/ConfigUtils';
import type {
    IInventoryStatusSorting,
    IProdcatConfig,
    IProductBenefitTypes,
    ISizePickerImages
} from '../../internal/interfaces/IProdcat';
import type { IFilterData } from '../../internal/views/product-filters/IProductFilters';
import type { IFiltersConfig } from '../../internal/interfaces/IFilters';
import type { IProductImages } from '../../internal/interfaces/IProductImage';
import type { IBreakpoints } from '../../internal/interfaces/IBreakpoints';
import type { IOrderConfigCollection } from '../../internal/interfaces/IOrderableComponents';
import type { IGridActionsBarConfig } from 'src/internal/views/product-grid/GridHeaderBarWrapper';
import type { IItem } from '~api/interfaces/IItem';
import type { IParamsById } from '~api/interfaces/IParamsById';
import {
    ProductGridCarousel,
    IProductGridCarousel
} from '../../internal/views/product-grid/ProductGridCarousel';
import {
    IProductGridContent,
    ProductGridContent
} from '../../internal/views/product-grid/ProductGridContent';
import { ComponentTypes } from '~constants/ComponentTypes';
import { ProductGridHeader } from '../../internal/views/product-grid/ProductGridHeader';

export interface IContentItem {
    nId: number | string;
    nPsn: number;
    nSize: contentSize;
}

export interface ITranslationsOverwrites {
    quickViewButtonLabel?: string;
    ctaButtonLabel?: string;
    pressedCtaButtonLabel?: string;
}

export interface ICTAConfig {
    enabled?: boolean;
    label?: string;
    absoluteUrl?: boolean;
    url?: string;
    newTab?: boolean;
    ctaCustomStyleClassName?: string;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
}

export interface IProductGridConfig extends IProdcatConfig {
    hideReadReviewsLink?: boolean;
    items?: IItem[];
    paramsById?: IParamsById;
    featuredProductId?: string;
    viewableInventoryStatuses?: string[];
    productTags?: string[];
    content?: IContentItem[];
    productGridBreakpoints: IBreakpoints;
    enableGridCarousel?: boolean;
    carouselSettings?: ISliderSettings;
    showSubDisplayName?: boolean;
    toutWidth?: number;
    toutHeight?: number;
    ctaType?: string;
    enableNotifyMe?: boolean;
    enableQuickView?: boolean;
    hideSorting?: boolean;
    hideReviewsNumber?: boolean;
    disableFeaturedProduct?: boolean;
    filterData?: IFilterData;
    featuredProductOverrideImage?: string;
    gridAccordionIconType?: IconTypes;
    enableAccordion?: boolean;
    accordionDefaultOpen?: boolean;
    accordionTitle?: string;
    accordionSubtext?: string;
    enableMobileQuickView?: boolean;
    productGridHeader?: string;
    productGridSubheader?: string;
    sizePickerImages?: ISizePickerImages;
    enableCategoryHeader?: boolean;
    showDynamicCategoryHeader?: boolean;
    translationsOverwrites?: ITranslationsOverwrites;
    gridCTA?: ICTAConfig;
    productImages?: IProductImages;
    lazyloadProductGridItems?: boolean;
    inventoryStatusSorting?: IInventoryStatusSorting;
    productBenefitTypes?: IProductBenefitTypes;
    enableProgressiveImages?: boolean;
    lazyLoadRootMargin?: string;
    filters?: IFiltersConfig;
    showIncludeTaxLabel?: boolean;
    componentElementsOrderConfig?: { [key: string]: IOrderConfigCollection };
    loadCarouselFirstSlide?: boolean;
    gridActionsBar?: IGridActionsBarConfig;
    categoryHeaderTag?: string;
    gridSource?: string;
}

export interface IFiltersTranslation {
    filtersLabel: string;
    clearFiltersLabel: string;
    applyFiltersLabel: string;
    filterByCategoryLabel: string;
    filterByPriceLabel: string;
    resultsLabel: string;
    filterByLabel: string;
    removeIconAriaLabel: string;
    collapseIconAriaLabel: string;
    expandIconAriaLabel: string;
}

export interface IProductGridWrapper {
    config: IProductGridConfig;
    className?: string;
    translations?: IFiltersTranslation;
    viewController: ProductGridViewController;
}

export const StyledHeaderColumn = styled(Column)`
    display: flex;
    flex-direction: column;
    align-items: center;
`;
@addContext()
@addConfig(
    ['prodcatConfig', 'features', 'itemMaxQuantity', 'sizePicker', 'gridActionsBar'],
    mergeConfigsFor(ComponentTypes.PRODUCT_GRID)
)
@configConsumer([
    'productGridBreakpoints',
    'enableGridCarousel',
    'carouselSettings',
    'accordionDefaultOpen',
    'enableAccordion',
    'features',
    'backorder',
    'inventoryStatusSorting'
])
@observer
class ProductGridWrapper extends React.Component<IProductGridWrapper> {
    public static displayName: string;
    public gridRef: React.RefObject<HTMLDivElement> = React.createRef();

    public async componentDidMount() {
        await this.props.viewController.loaded(this.props);
    }

    async componentDidUpdate() {
        if (!this.props.viewController.isLoaded) {
            await this.props.viewController.loaded(this.props);
        }
    }

    private getProductGridContentProps = (): IProductGridContent => {
        const {
            data: {
                featuredProductId,
                isAllGiftsFilterApplied,
                appliedFilters,
                productsNumber,
                isFilterDisplayStacked,
                areFiltersApplied,
                productSortingDropdownValue,
                scrollTopOffset,
                gridItems
            },
            filters,
            totalAppliedFilters,
            extractedAppliedFilters,
            positionGridInsideAccordion,
            setProductSortingDropdownValue,
            isLoaded
        } = this.props.viewController;

        return {
            featuredProductId,
            isAllGiftsFilterApplied,
            appliedFilters,
            productsNumber,
            isFilterDisplayStacked,
            areFiltersApplied,
            productSortingDropdownValue,
            filters,
            scrollTopOffset,
            totalAppliedFilters,
            extractedAppliedFilters,
            positionGridInsideAccordion,
            isLoaded,
            gridItems,
            setProductSortingDropdownValue
        };
    };

    private getProductGridCarouselProps = (): IProductGridCarousel => {
        const {
            positionGridInsideAccordion,
            isLoaded,
            setGridPosition,
            data: { gridItems, featuredProductId, categoryTag }
        } = this.props.viewController;

        const { carouselSettings } = this.props.config;

        return {
            positionGridInsideAccordion,
            isLoaded,
            setGridPosition,
            gridItems,
            featuredProductId,
            categoryTag,
            carouselSettings
        };
    };

    public render() {
        const { enableGridCarousel, enableAccordion } = this.props.config;

        const isLoaded = this.props.viewController.isLoaded;

        if (!isLoaded) {
            return null;
        }

        if (enableGridCarousel) {
            return <ProductGridCarousel {...this.getProductGridCarouselProps()} />;
        }

        const { categoryTag } = this.props.viewController.data;
        const productGridContent = <ProductGridContent {...this.getProductGridContentProps()} />;

        return (
            <>
                <ProductGridHeader categoryTag={categoryTag} isWrappedByContainer />
                {enableAccordion ? (
                    <ProductAccordion>{productGridContent}</ProductAccordion>
                ) : (
                    productGridContent
                )}
                <CartLimitReachedModal />
            </>
        );
    }
}

ProductGridWrapper.displayName = 'ProductGrid';

export default ProductGridWrapper;
